import { BodyType, type SimCollider, type SimGo } from '../world.ts';
import type { GameData, SpriteRef } from '../../../data/gameData.ts';
import { apply } from '../../../model/xform.ts';
import type { RGBA, V2 } from '../unity.ts';
import { PlayerController } from '../player.ts';

/** Util.IsPlayer(collider.gameObject) */
export function playerOf(c: SimCollider | SimGo | null | undefined): PlayerController | null {
  if (!c) return null;
  const go = 'go' in c ? c.go : c;
  return go.getBehaviour(PlayerController);
}

/** SpriteRenderer.bounds (world AABB) */
export function spriteBounds(go: SimGo): [number, number, number, number] | null {
  const s = go.sprite;
  if (!s?.sprite) return null;
  const def = s.sprite.def;
  const ppu = s.sprite.texDef.ppu;
  const w = s.drawMode ? s.size[0] : def.r[2] / ppu;
  const h = s.drawMode ? s.size[1] : def.r[3] / ppu;
  const px = s.drawMode ? 0.5 : def.p[0], py = s.drawMode ? 0.5 : def.p[1];
  const m = go.worldXform();
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [lx, ly] of [[-px * w, -py * h], [(1 - px) * w, -py * h], [-px * w, (1 - py) * h], [(1 - px) * w, (1 - py) * h]]) {
    const [x, y] = apply(m, lx, ly);
    x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  }
  return [x0, y0, x1, y1];
}

export function setAlpha(go: SimGo | null | undefined, a: number) {
  if (go?.sprite) go.sprite.color = [go.sprite.color[0], go.sprite.color[1], go.sprite.color[2], a];
}

export function withAlpha(c: RGBA, a: number): RGBA {
  return [c[0], c[1], c[2], a];
}

export function rectContains(r: { x: number; y: number; w: number; h: number }, p: V2): boolean {
  return p.x >= r.x && p.x < r.x + r.w && p.y >= r.y && p.y < r.y + r.h;
}

/** all colliders in a GameObject's subtree (GetComponentsInChildren<Collider2D>(true)) */
export function collidersIn(go: SimGo): SimCollider[] {
  return go.collidersInChildren();
}

/** GetComponentsInChildren<Collider2D>() (active objects, disabled components included) */
export function activeCollidersIn(go: SimGo): SimCollider[] {
  const out: SimCollider[] = [];
  const walk = (g: SimGo) => {
    if (!g.active) return;
    out.push(...g.colliders);
    g.children.forEach(walk);
  };
  walk(go);
  return out;
}

export function spriteRef(gd: GameData, r: any): SpriteRef | null {
  if (!r?.guid) return null;
  const hit = gd.spriteById.get(`${r.guid}:${r.fileID}`);
  if (hit) return hit;
  const tex = gd.sprites.textures[r.guid];
  return tex && tex.sprites.length === 1 ? { tex: r.guid, texDef: tex, def: tex.sprites[0] } : null;
}

/** an attached rigidbody that is dynamic, kinematic or a player (respawn / solidify blockers) */
export function blocksRespawn(c: SimCollider, includeKinematic: boolean): boolean {
  const rb = c.attachedRigidbody;
  if (!rb) return false;
  return rb.type === BodyType.Dynamic || (includeKinematic && rb.type === BodyType.Kinematic) || !!playerOf(c);
}
